import type { ExtractedDate } from "@/types/extraction"

const DAY_MS = 1000 * 60 * 60 * 24

/** The axis always reaches at least a week past `now`, so a near-term-only bid still reads as a span. */
const AXIS_LOOKAHEAD_MS = 7 * DAY_MS

/** Breathing room added to each end of the raw span, as a fraction of it. */
const AXIS_PAD_RATIO = 0.03

/** Two markers closer than this (in percentage points) can't share a lane without colliding. */
const LANE_MIN_GAP_PCT = 8

export type KeyDatesViewMode = "timeline" | "table"

/**
 * Storage key for the Key Dates view choice.
 *
 * Per user AND per bid, exactly like `filterStorageKey`: per user because a
 * shared browser must not hand the next account the previous one's view, and
 * per bid because a schedule-heavy tender is worth reading as a table without
 * forcing the table onto every other bid.
 */
export function keyDatesViewModeStorageKey(
  userId: string | null | undefined,
  bidId: string,
): string {
  return `key-dates-view-mode:${userId ?? "anon"}:${bidId}`
}

/** Anything that is not one of the two modes reads as "never chosen" → timeline. */
export function readKeyDatesViewMode(key: string): KeyDatesViewMode {
  try {
    const raw = window.localStorage.getItem(key)
    return raw === "table" || raw === "timeline" ? raw : "timeline"
  } catch {
    return "timeline"
  }
}

export interface TimelineMarker {
  id: string
  leftPct: number
  lane: number
  item: ExtractedDate
}

export interface KeyDatesTimelineLayout {
  axisStartIso: string
  axisEndIso: string
  todayPct: number
  markers: TimelineMarker[]
  unplaced: ExtractedDate[]
}

interface DatedItem {
  id: string
  time: number
  index: number
  item: ExtractedDate
}

/** An item is placeable only if it has an id to key on and an absolute, parseable date. */
function toDatedItem(item: ExtractedDate, index: number): DatedItem | null {
  if (!item.id || !item.dateIso) return null
  const time = new Date(item.dateIso).getTime()
  if (Number.isNaN(time)) return null
  return { id: item.id, time, index, item }
}

function round(value: number): number {
  return Math.round(value * 1000) / 1000
}

/**
 * Lays out extracted key dates on a horizontal timeline.
 *
 * Pure: `now` is a parameter, never read from the clock, so the same inputs
 * always produce the same layout (and tests need no fake timers).
 *
 * The axis runs from `min(earliest date, now)` to `max(latest date, now + 7d)`,
 * then gains 3% of that raw span at each end. Positions are computed against the
 * padded span, so the outermost marker lands near — but never on — 0% / 100%,
 * leaving room for its label. Markers are ordered by date (a submission deadline
 * wins a tie, then input order) and greedily assigned to the first lane whose
 * last marker sits at least 8 percentage points to the left.
 *
 * Items without an absolute parseable date — relative wording like "14 days
 * after award", or no date at all — and items with no id are returned in
 * `unplaced`, in input order, for the caller to list beside the timeline.
 */
export function layoutKeyDatesTimeline(
  items: ExtractedDate[],
  now: Date,
): KeyDatesTimelineLayout {
  const dated: DatedItem[] = []
  const unplaced: ExtractedDate[] = []

  items.forEach((item, index) => {
    const datedItem = toDatedItem(item, index)
    if (datedItem) dated.push(datedItem)
    else unplaced.push(item)
  })

  const nowMs = now.getTime()
  const times = dated.map((d) => d.time)
  const rawStart = Math.min(nowMs, ...times)
  const rawEnd = Math.max(nowMs + AXIS_LOOKAHEAD_MS, ...times)

  // rawEnd is at least now + 7d and rawStart at most now, so the span is never zero.
  const rawSpan = rawEnd - rawStart
  const pad = rawSpan * AXIS_PAD_RATIO
  const axisStart = rawStart - pad
  const axisEnd = rawEnd + pad
  const axisSpan = axisEnd - axisStart

  const pctFor = (time: number): number => round(((time - axisStart) / axisSpan) * 100)

  const sorted = [...dated].sort((a, b) => {
    if (a.time !== b.time) return a.time - b.time
    const aDeadline = a.item.isSubmissionDeadline ? 0 : 1
    const bDeadline = b.item.isSubmissionDeadline ? 0 : 1
    if (aDeadline !== bDeadline) return aDeadline - bDeadline
    return a.index - b.index
  })

  const laneLastPct: number[] = []
  const markers: TimelineMarker[] = sorted.map((d) => {
    const leftPct = pctFor(d.time)
    let lane = laneLastPct.findIndex((last) => leftPct - last >= LANE_MIN_GAP_PCT)
    if (lane === -1) lane = laneLastPct.length
    laneLastPct[lane] = leftPct
    return { id: d.id, leftPct, lane, item: d.item }
  })

  return {
    axisStartIso: new Date(axisStart).toISOString(),
    axisEndIso: new Date(axisEnd).toISOString(),
    todayPct: pctFor(nowMs),
    markers,
    unplaced,
  }
}
